import { unionWith, find, get, sortBy } from 'lodash-es';

const getParty = ({ subscriber }) => subscriber || undefined;

const getActions = status => {
  switch (status) {
    case 'active':
    case 'quote': {
      return [
        {
          route: '/contracts/',
          action: 'view',
          type: 'link',
          label: 'contracts.grid.cell.actions.btn.view.label',
        },
      ];
    }
    default: {
      return [
        {
          route: '/contracts/',
          action: 'view',
          type: 'link',
          label: 'contracts.grid.cell.actions.btn.view.label',
        },
      ];
    }
  }
};

// export const filterCurrentMonth = date => {
//   const startOfMonth = moment()
//     .startOf('month')
//     .format('YYYY-MM-DD');
//   const endOfMonth = moment()
//     .endOf('month')
//     .format('YYYY-MM-DD');

//   return date >= startOfMonth && date <= endOfMonth;
// };

const isWithinLastMonth = (dateStr) => {
  if (!dateStr) return false;
  const toMidnight = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
  const target = toMidnight(dateStr);
  if (isNaN(target.getTime())) return false;
  const today = toMidnight(new Date());
  const oneMonthAgo = new Date(today); oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  return target >= oneMonthAgo && target <= today;
};


export const getContractItem = contract => {
  const party = getParty(contract);
  return {
    id: contract.id,
    contractNumber: contract.contractNumber,
    quoteNumber: contract.quoteNumber,
    status: contract.contractStatus,
    // code: contract.quote_code,
    createdDate: contract.createDate,
    signatureDate: contract.signatureDate,
    // writeDate: contract.write_date,
    // appliableConditionsDate: contract.appliable_conditions_date,
    date: contract.startDate,
    commercialProductId: contract.commercialProduct.id ? contract.commercialProduct.id : undefined,
    commercialProductName: contract.commercialProduct.name ? contract.commercialProduct.name : undefined,
    productId: contract.product.id,
    party,
    partyName: party ? `${party.firstName ? party.firstName : ''} ${party.name}` : undefined,
    partyId: party?.id,
    actions: getActions(contract.status),
    isOutstanding: contract?.isOutstanding ?? false,
    isUnpaid: contract?.isUnpaid ?? false,
    currentMonth: isWithinLastMonth(contract?.lastTermEndDate),
  };
};

export const getContractList = contracts => {
  try {
    return contracts?.map(contract => getContractItem(contract));
  } catch (e) {
    console.error(e)
    return [];
  }
};

// eslint-disable-next-line no-unused-vars
export const updateContractList = (contractList, contractItem) => {
  try {
    const item = getContractItem(contractItem);
    // Find item index using _.findIndex (thanks @AJ Richardson for comment)
    return unionWith([item], contractList, (l, r) => l.id === r.id);
  } catch (e) {
    return contractList;
  }
};

// eslint-disable-next-line camelcase, no-unused-vars
const getCoveredElement = ({ coveredElement, covered_elements }) => {
  if (typeof coveredElement === 'undefined' && typeof coveredElement.id === 'undefined') return undefined;
  const { id } = coveredElement;
  // eslint-disable-next-line no-underscore-dangle
  const _coveredElement = find(covered_elements, ['id', id]);
  return _coveredElement
    ? {
      id,
      recName: _coveredElement.rec_name,
    }
    : coveredElement;
};

// eslint-disable-next-line camelcase
const getOption = ({ option, covered_element_options, covered_elements }) => {
  if (typeof option === 'undefined' && typeof option.id === 'undefined') return undefined;
  const { id } = option;
  // eslint-disable-next-line no-underscore-dangle
  const _option = find(covered_element_options, { id });
  return _option
    ? {
      id,
      recName: _option.rec_name,
      coveredElement: getCoveredElement({
        coveredElement: _option.covered_element,
        covered_elements,
      }),
    }
    : option;
};

const getLoan = ({ loan, loans }) => {
  if (typeof loan === 'undefined' && typeof loan.id === 'undefined') return undefined;
  const { id } = loan;
  // eslint-disable-next-line no-underscore-dangle
  const _loan = find(loans, ['id', id]) || {};
  return {
    id,
    amount: _loan.amount,
    kind: _loan.kind,
    recName: _loan.rec_name,
    rate: _loan.rate,
    state: _loan.state,
    number: _loan.number,
  };
};

export const getPremiumContract = contract => {
  if (typeof contract === 'undefined' && typeof contract.all_premiums === 'undefined') return undefined;
  let premiums = [];
  const {
    is_loan: isLoan,
    all_premiums: allPremiums,
    covered_element_options: coveredElementOptions,
    loans = [],
    covered_elements: coveredElements,
  } = contract;

  if (!isLoan && Array.isArray(allPremiums)) {
    premiums = allPremiums.map(premium => ({
      id: premium.id,
      amount: premium.amount,
      recName: premium.rec_name,
      frequency: premium.frequency,
      option: getOption({
        option: premium.option,
        covered_element_options: coveredElementOptions,
        covered_elements: coveredElements,
      }),
      loan: getLoan({ loan: premium.loan, loans }),
    }));
  } else if (isLoan && coveredElements && coveredElementOptions) {
    loans.forEach(loan => {
      coveredElements.forEach((covElem, idx) => {
        const covOptions = sortBy(
          coveredElementOptions.filter(cov => get(cov, 'covered_element.id') === covElem.id),
          ['id']
        );

        premiums = [
          ...premiums,
          ...covOptions.map(covOpt => ({
            id: idx,
            amount: 0,
            recName: `${covOpt.rec_name} (${covElem.rec_name})`,
            frequency: '',
            option: {
              id: covOpt.id,
              recName: covOpt.rec_name,
              coveredElement: {
                id: covElem.id,
                recName: covElem.rec_name,
              },
            },
            loan: {
              id: loan.id,
              amount: loan.amount,
              kind: loan.kind,
              recName: loan.rec_name,
              rate: loan.rate,
              state: loan.state,
              number: loan.number,
            },
          })),
        ];
      });
    });
  }

  return premiums;
};

export const getReducedContract = ({ nextContract, prevContract }) => {
  const { beneficiaries, events, documents } = typeof prevContract !== 'undefined' ? prevContract.toJS() : {};
  if (typeof nextContract === 'undefined') return undefined;
  return {
    ...nextContract,
    // premiumsContract: getPremiumContract(nextContract),
    covered_elements: nextContract.covered_elements,
    // is_loan: nextContract.is_loan,
    id: nextContract.id,
    subscriber: nextContract.subscriber,
    // appliable_conditions_date: nextContract.appliable_conditions_date,
    status: nextContract.status,
    // current_state: nextContract.current_state,
    // current_state_name: nextContract.current_state_name,
    underwritings: nextContract.underwritings,
    // beneficiaries: beneficiaries || nextContract.beneficiaries,
    // events: events || nextContract.events,
    documents: documents || nextContract.documents,
    product: get(nextContract, 'product'),
    // com_product: get(nextContract, 'com_product'),
  };
};

export const appendContractList = (contractList, contracts2) => {
  try {
    const contractList2 = contracts2.map(party => getContractItem(party));
    return contractList.toJS().concat(contractList2);
  } catch (e) {
    return [];
  }
};
